import { Download as DownloadIcon, Send as SendIcon } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ProposalService } from "../../../shared/services/api/proposal/ProposalService";
import { maskCpf } from "../../../shared/utils/globalMask-document";
import { exportExcelWithStyle } from "../../finan/utils/exportExcel";
import { RowDataCertificate } from "../types";
import { CERTIFICADO_STATUS_META } from "../utils/certificadoStatus";
import { StatTile } from "./StatTile";

interface ICertificadoResendModalProps {
	open: boolean;
	rows: RowDataCertificate[];
	onClose: () => void;
	onRowUpdate: (rowNumber: number, status: RowDataCertificate["status"], mensagem: string, proposalIds?: string[]) => void;
}

export const CertificadoResendModal: React.FC<ICertificadoResendModalProps> = ({ open, rows, onClose, onRowUpdate }) => {
	const [isProcessing, setIsProcessing] = useState(false);
	const [currentRowNumber, setCurrentRowNumber] = useState<number | null>(null);
	const [processedCount, setProcessedCount] = useState(0);
	const [summary, setSummary] = useState<{ enviado: number; nao_encontrado: number; multiplas: number; erro: number } | null>(null);
	const [isExporting, setIsExporting] = useState(false);

	const total = rows.length;
	const progressPercent = total > 0 ? Math.round((processedCount / total) * 100) : 0;

	const handleStart = async () => {
		setIsProcessing(true);
		setProcessedCount(0);
		setSummary(null);

		let enviado = 0;
		let nao_encontrado = 0;
		let multiplas = 0;
		let erro = 0;

		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			setCurrentRowNumber(row.RowNumber);
			onRowUpdate(row.RowNumber, "buscando", "Buscando garantia ativa...");

			if (i > 0) {
				await new Promise((resolve) => setTimeout(resolve, 600));
			}

			try {
				const result = await ProposalService.getAll(1, row.cpf, "documento", "", "active", "", "", "", "", "0", 5);

				if (result instanceof Error) {
					erro++;
					onRowUpdate(row.RowNumber, "erro", "Erro ao buscar garantia ativa.");
				} else if (!result.data || result.data.length === 0) {
					nao_encontrado++;
					onRowUpdate(row.RowNumber, "nao_encontrado", "Nenhuma garantia ativa encontrada para este CPF.");
				} else if (result.data.length > 1) {
					multiplas++;
					onRowUpdate(
						row.RowNumber,
						"multiplas",
						`${result.data.length} garantias ativas encontradas — revisar manualmente.`,
						result.data.map((p) => p.id)
					);
				} else {
					const proposalId = result.data[0].id;
					const sendResult = await ProposalService.sendCertificatePartnerWebhook(proposalId);
					if (sendResult instanceof Error) {
						erro++;
						onRowUpdate(row.RowNumber, "erro", "Erro ao enviar certificado ao parceiro.", [proposalId]);
					} else {
						enviado++;
						onRowUpdate(row.RowNumber, "enviado", "Certificado enviado ao parceiro com sucesso.", [proposalId]);
					}
				}
			} catch (error) {
				erro++;
				onRowUpdate(row.RowNumber, "erro", "Erro inesperado ao processar este CPF.");
			}

			setProcessedCount(i + 1);
		}

		setIsProcessing(false);
		setCurrentRowNumber(null);
		setSummary({ enviado, nao_encontrado, multiplas, erro });
	};

	const handleExportReport = async () => {
		setIsExporting(true);
		try {
			const data = rows.map((row) => ({
				linha: (row.RowNumber + 1).toString().padStart(3, "0"),
				cpf: maskCpf(row.cpf),
				situacao: CERTIFICADO_STATUS_META[row.status].label,
				detalhes: row.mensagem,
				propostas: row.proposalIds?.join(", ") ?? "",
			}));

			await exportExcelWithStyle({
				data,
				columns: [
					{ key: "linha", label: "#" },
					{ key: "cpf", label: "CPF" },
					{ key: "situacao", label: "Situação" },
					{ key: "detalhes", label: "Detalhes" },
					{ key: "propostas", label: "Propostas" },
				],
				title: "Relatório de Reenvio de Certificado",
				subtitle: `Enviados: ${summary?.enviado ?? 0} | Não encontrados: ${summary?.nao_encontrado ?? 0} | Múltiplas garantias: ${
					summary?.multiplas ?? 0
				} | Erros: ${summary?.erro ?? 0}`,
				sheetName: "Certificados",
				fileName: `relatorio_certificado_${new Date().toISOString().slice(0, 10)}.xlsx`,
			});
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Dialog open={open} onClose={isProcessing ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Stack direction="row" spacing={1} alignItems="center">
					<SendIcon color="primary" fontSize="small" />
					<span>Reenvio de Certificado {total > 0 && `(${total})`}</span>
				</Stack>
				{isProcessing && (
					<Box mt={1.5}>
						<Typography variant="body2" color="primary" gutterBottom>
							Processando {processedCount + 1} de {total} ({progressPercent}%)
						</Typography>
						<LinearProgress variant="determinate" value={progressPercent} />
					</Box>
				)}
			</DialogTitle>
			<DialogContent>
				{summary && (
					<Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mb={2}>
						<StatTile label="Enviados" value={summary.enviado} color="success.main" />
						<StatTile label="Não Encontrados" value={summary.nao_encontrado} color="text.secondary" />
						<StatTile label="Múltiplas Garantias" value={summary.multiplas} color="warning.main" />
						<StatTile label="Erros" value={summary.erro} color="error.main" />
					</Stack>
				)}

				<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell>#</TableCell>
								<TableCell>CPF</TableCell>
								<TableCell>Situação</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((row) => {
								const isCurrent = currentRowNumber === row.RowNumber;
								const meta = CERTIFICADO_STATUS_META[row.status];
								return (
									<TableRow key={row.RowNumber} hover selected={isCurrent}>
										<TableCell>#{(row.RowNumber + 1).toString().padStart(3, "0")}</TableCell>
										<TableCell>{maskCpf(row.cpf)}</TableCell>
										<TableCell>
											<Chip size="small" icon={meta.icon} label={meta.label} color={meta.color} variant={isCurrent ? "filled" : "outlined"} />
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary" disabled={isProcessing}>
					Fechar
				</Button>
				{summary && (
					<Button
						onClick={handleExportReport}
						color="primary"
						variant="outlined"
						startIcon={<DownloadIcon />}
						disabled={isProcessing || isExporting}
					>
						{isExporting ? "Exportando..." : "Exportar Relatório"}
					</Button>
				)}
				<Button onClick={handleStart} color="primary" variant="contained" disabled={isProcessing || total === 0}>
					{isProcessing ? "Processando..." : "Iniciar Envio"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
